// Helper to safely access environment variables in browser and Node.js
const getEnv = (key: string, defaultValue: string): string => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue;
  }
  return defaultValue;
};

/**
 * Read a URL search param at module load time (browser only, non-production).
 * Allows e2e tests to override specific CONFIG values per page by navigating to
 * `/?e2e_capacity=3` without needing a separate webpack build.
 * Returns null on server / in production.
 */
const e2eUrlParam = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
};

export const CONFIG = {
  // Chatroom settings
  // Capacity C (one rule for the web bundle, the Node server and the embedded apps): the size of
  // each Nearby list and of a Place.
  //   release (default)  -> 498
  //   local debug        -> 3   (webpack `--mode development` and the dev:* scripts bake/export it)
  //   tests              -> whatever they pass explicitly (CHATROOM_MAX_CAPACITY env or `?e2e_capacity=`)
  // The `process.env.X` reads are static on purpose so webpack can inline them in the browser bundle.
  CHATROOM_MAX_CAPACITY: parseInt(
    e2eUrlParam('e2e_capacity') || process.env.CHATROOM_MAX_CAPACITY || '498',
    10,
  ),
  /** Incoming, distinct Friend marks required to create the identity's one custom room. */
  PLACE_CREATE_MIN_FRIEND_MARKS: parseInt(
    e2eUrlParam('e2e_place_min_friends')
      || process.env.PLACE_CREATE_MIN_FRIEND_MARKS
      || (process.env.DISABLE_HMR === 'true' ? '0' : '12'),
    10,
  ),
  /** `3` means zero, one or two received blocks are allowed. */
  PLACE_CREATE_MAX_BLOCKS_EXCLUSIVE: parseInt(
    e2eUrlParam('e2e_place_max_blocks') || process.env.PLACE_CREATE_MAX_BLOCKS_EXCLUSIVE || '3',
    10,
  ),
  GLOBAL_CHATROOM_ID: 'global',
  /** Non-geographic overflow family for Global participants who have no confirmed location. */
  GLOBAL_UNKNOWN_CHATROOM_ID: 'global-unknown',
  /** OSM vector basemap used by the lazy MapLibre chatroom view. Override for self-hosting. */
  CHATROOM_MAP_STYLE_URL:
    typeof process !== 'undefined' && process.env?.IINPUBLIC_MAP_STYLE_URL
      ? process.env.IINPUBLIC_MAP_STYLE_URL
      : 'https://tiles.openfreemap.org/styles/liberty',

  // Bulk sending limits
  MAX_BULK_RECIPIENTS: 1000,
  DEFAULT_BULK_LIMIT: 100,

  // Location privacy
  LOCATION_BLUR_RADIUS: 1000, // meters
  MAX_LOCATION_PRECISION: 100, // meters

  // Rate limiting
  RATE_LIMITS: {
    TALK_SEND_DAILY: 10,
    TALK_SEND_WEEKLY: 50,
    MESSAGE_PER_MINUTE: 60,
    BULK_SEND_DAILY: 5,
  },
  /** Min milliseconds between a user's send/receive talk edges (0 = off). Env: IINPUBLIC_SYMMETRIC_TALK_EDGE_COOLDOWN_MS */
  SYMMETRIC_TALK_EDGE_COOLDOWN_MS: Math.max(
    0,
    parseInt(getEnv('IINPUBLIC_SYMMETRIC_TALK_EDGE_COOLDOWN_MS', '0'), 10) || 0,
  ),

  // Talk structure
  MAX_QUESTIONS_PER_TALK: 20,
  MAX_ANSWERS_PER_QUESTION: 10,
  MAX_TALK_DEPTH: 10,

  // Content filtering
  GRAMMAR_THRESHOLD: 0.7,
  DIRTY_WORDS_STRICTNESS: 'moderate',

  // Reputation
  MIN_REPUTATION_FOR_BULK: -10,
  BLOCK_IMPACT_MULTIPLIER: 2,

  // Performance
  MESSAGE_HISTORY_LIMIT: 1000,
  CONVERSATION_EXPIRY_DAYS: 30,

  // Survey
  MIN_SURVEY_RESPONSES: 5,
  MAX_SURVEY_QUESTIONS: 15,

  // Age verification
  MIN_AGE_FOR_ADULT_CONTENT: 18,
  AGE_VERIFICATION_THRESHOLD: 3, // votes needed

  // Development
  DEBUG_MODE: getEnv('NODE_ENV', 'development') !== 'production',
  LOG_LEVEL: getEnv('LOG_LEVEL', 'info'),
} as const;
